import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { BlogService } from './blog.service';
import { NotificationService } from '../../core/notification.service';
import { CommonModule } from '@angular/common';

@Component({
  standalone: false,
  selector: 'app-blog-form',
  template: `
    <div class="max-w-4xl bg-white p-6 rounded shadow">
      <h2 class="text-2xl font-semibold mb-6">{{ isEditMode ? 'Editar Artigo' : 'Novo Artigo' }}</h2>

      <form [formGroup]="form" (ngSubmit)="onSubmit()" class="space-y-4">
        <!-- Título -->
        <div>
          <label class="block text-sm font-medium mb-1">Título *</label>
          <input formControlName="title" class="w-full border rounded px-3 py-2" placeholder="Título do artigo" maxlength="200" />
          <div *ngIf="form.get('title')?.invalid && form.get('title')?.touched" class="text-sm text-red-600 mt-1">
            Título é obrigatório (máx. 200 caracteres)
          </div>
        </div>

        <!-- Slug -->
        <div>
          <label class="block text-sm font-medium mb-1">Slug *</label>
          <input formControlName="slug" class="w-full border rounded px-3 py-2" placeholder="slug-do-artigo" maxlength="200" />
          <p class="text-xs text-muted mt-1">URL amigável (gerada automaticamente do título)</p>
          <div *ngIf="form.get('slug')?.invalid && form.get('slug')?.touched" class="text-sm text-red-600 mt-1">
            Slug é obrigatório e único (máx. 200 caracteres)
          </div>
        </div>

        <!-- Categoria -->
        <div>
          <label class="block text-sm font-medium mb-1">Categoria *</label>
          <select formControlName="category" class="w-full border rounded px-3 py-2">
            <option value="dicas">Dicas</option>
            <option value="inspiracao">Inspiração</option>
            <option value="guias">Guias</option>
          </select>
        </div>

        <!-- Resumo -->
        <div>
          <label class="block text-sm font-medium mb-1">Resumo *</label>
          <textarea formControlName="excerpt" class="w-full border rounded px-3 py-2" rows="3" placeholder="Resumo do artigo (aparece na listagem)" maxlength="500"></textarea>
          <p class="text-xs text-muted mt-1">{{ form.get('excerpt')?.value?.length || 0 }}/500 caracteres</p>
          <div *ngIf="form.get('excerpt')?.invalid && form.get('excerpt')?.touched" class="text-sm text-red-600 mt-1">
            Resumo é obrigatório (máx. 500 caracteres)
          </div>
        </div>

        <!-- Conteúdo -->
        <div>
          <div class="flex items-center justify-between mb-1">
            <label class="block text-sm font-medium">Conteúdo *</label>
            <button type="button" (click)="openPreview()" class="text-xs px-2 py-1 border rounded hover:bg-gray-100">
              👁️ Preview
            </button>
          </div>
          <textarea formControlName="content" class="w-full border rounded px-3 py-2 font-mono text-xs" rows="20" placeholder="Conteúdo completo do artigo (HTML)"></textarea>
          <p class="text-xs text-muted mt-1">Use HTML para formatar o conteúdo</p>
          <div *ngIf="form.get('content')?.invalid && form.get('content')?.touched" class="text-sm text-red-600 mt-1">
            Conteúdo é obrigatório
          </div>
        </div>

        <!-- Imagem Destacada -->
        <div>
          <label class="block text-sm font-medium mb-1">URL da Imagem Destacada</label>
          <input formControlName="featuredImage" type="url" class="w-full border rounded px-3 py-2" placeholder="https://exemplo.com/imagem.jpg" maxlength="500" />
          <div *ngIf="form.get('featuredImage')?.value" class="mt-2">
            <img [src]="form.get('featuredImage')?.value" alt="Preview" class="max-w-xs rounded border" />
          </div>
        </div>

        <!-- Grid 2 colunas -->
        <div class="grid grid-cols-2 gap-4">
          <!-- Autor -->
          <div>
            <label class="block text-sm font-medium mb-1">Autor</label>
            <input formControlName="author" class="w-full border rounded px-3 py-2" placeholder="Nome do autor" maxlength="100" />
          </div>

          <!-- Data de Publicação -->
          <div>
            <label class="block text-sm font-medium mb-1">Data de Publicação</label>
            <input formControlName="publishedAt" type="datetime-local" class="w-full border rounded px-3 py-2" />
            <p class="text-xs text-muted mt-1">Deixe em branco para usar agora</p>
          </div>
        </div>

        <!-- Tags -->
        <div>
          <label class="block text-sm font-medium mb-1">Tags</label>
          <input formControlName="tags" class="w-full border rounded px-3 py-2" placeholder="vestido, noiva, casamento" />
          <p class="text-xs text-muted mt-1">Separe as tags por vírgula</p>
        </div>

        <!-- SEO -->
        <div class="border-t pt-4">
          <h3 class="text-lg font-medium mb-3">SEO</h3>
          
          <!-- Meta Title -->
          <div class="mb-4">
            <label class="block text-sm font-medium mb-1">Meta Título</label>
            <input formControlName="metaTitle" class="w-full border rounded px-3 py-2" placeholder="Título otimizado para motores de busca" maxlength="200" />
            <p class="text-xs text-muted mt-1">{{ form.get('metaTitle')?.value?.length || 0 }}/200 caracteres</p>
          </div>

          <!-- Meta Description -->
          <div>
            <label class="block text-sm font-medium mb-1">Meta Descrição</label>
            <textarea formControlName="metaDescription" class="w-full border rounded px-3 py-2" rows="2" placeholder="Descrição otimizada para motores de busca" maxlength="500"></textarea>
            <p class="text-xs text-muted mt-1">{{ form.get('metaDescription')?.value?.length || 0 }}/500 caracteres</p>
          </div>
        </div>

        <!-- Publicado -->
        <div class="flex items-center gap-2">
          <input type="checkbox" formControlName="isPublished" id="isPublished" class="rounded" />
          <label for="isPublished" class="text-sm font-medium cursor-pointer">Publicar artigo</label>
          <span class="text-xs text-muted">(Artigos não publicados não aparecem no site)</span>
        </div>

        <!-- Botões -->
        <div class="flex gap-3 pt-4">
          <button type="submit" [disabled]="form.invalid || saving" class="bg-primary text-white px-6 py-2 rounded hover:bg-primary/90 disabled:opacity-50">
            {{ saving ? 'Salvando...' : (isEditMode ? 'Atualizar' : 'Criar') }}
          </button>
          <button type="button" (click)="cancel()" class="px-6 py-2 border rounded hover:bg-gray-50">
            Cancelar
          </button>
        </div>
      </form>

      <!-- Modal Preview -->
      <div *ngIf="showPreview" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-screen overflow-auto mx-4">
          <div class="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
            <h3 class="text-lg font-semibold">Preview do Conteúdo</h3>
            <button (click)="closePreview()" class="text-gray-500 hover:text-gray-700 text-2xl">×</button>
          </div>
          <div class="p-6 preview-content">
            <div [innerHTML]="previewContent" class="
              prose 
              max-w-none 
              prose-headings:font-bold
              prose-h1:text-3xl
              prose-h2:text-2xl
              prose-h3:text-xl
              prose-h4:text-lg
              prose-h5:text-base
              prose-h6:text-base
              prose-p:mb-4
              prose-p:leading-7
              prose-strong:font-bold
              prose-em:italic
              prose-a:text-blue-600
              prose-a:underline
              prose-ul:list-disc
              prose-ul:ml-4
              prose-ul:mb-4
              prose-ol:list-decimal
              prose-ol:ml-4
              prose-ol:mb-4
              prose-li:mb-2
              prose-blockquote:border-l-4
              prose-blockquote:border-gray-300
              prose-blockquote:pl-4
              prose-blockquote:italic
              prose-blockquote:my-4
              prose-code:bg-gray-100
              prose-code:px-2
              prose-code:rounded
              prose-pre:bg-gray-900
              prose-pre:text-white
              prose-pre:p-4
              prose-pre:rounded
              prose-pre:overflow-x-auto
              prose-img:max-w-full
              prose-img:h-auto
              prose-img:rounded
              prose-img:my-4
              prose-table:border-collapse
              prose-table:w-full
              prose-th:bg-gray-100
              prose-th:border
              prose-th:p-2
              prose-td:border
              prose-td:p-2
              prose-hr:my-8
            "></div>
          </div>
          <div class="sticky bottom-0 bg-gray-50 border-t p-4 flex justify-end">
            <button (click)="closePreview()" class="px-4 py-2 border rounded hover:bg-gray-100">
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  `
})

export class BlogFormComponent implements OnInit {
  form!: FormGroup;
  isEditMode = false;
  postId?: string;
  saving = false;
  showPreview = false;

  constructor(
    private fb: FormBuilder,
    private svc: BlogService,
    private route: ActivatedRoute,
    private router: Router,
    private notification: NotificationService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!id;
    console.log('[BlogForm] ngOnInit - ID:', id, 'isEditMode:', this.isEditMode);
    
    // Tentar obter o post do state da navegação
    const navigation = this.router.getCurrentNavigation();
    const statePost = navigation?.extras?.state?.['post'] || history.state?.post;
    console.log('[BlogForm] State post:', statePost);
    
    if (id) {
      this.postId = id;
    }

    this.form = this.fb.group({
      title: ['', [Validators.required, Validators.maxLength(200)]],
      slug: ['', [Validators.required, Validators.maxLength(200)]],
      excerpt: ['', [Validators.required, Validators.maxLength(500)]],
      content: ['', Validators.required],
      featuredImage: ['', Validators.maxLength(500)],
      author: ['', Validators.maxLength(100)],
      publishedAt: [''],
      category: ['dicas', Validators.required],
      tags: [''],
      metaTitle: ['', Validators.maxLength(200)],
      metaDescription: ['', Validators.maxLength(500)],
      isPublished: [true]
    });

    // Auto-gerar slug ao digitar título (apenas em modo criação ou se slug não foi editado)
    this.form.get('title')?.valueChanges.subscribe(title => {
      if (!this.isEditMode || !this.form.get('slug')?.dirty) {
        const slug = this.svc.generateSlug(title);
        this.form.patchValue({ slug }, { emitEvent: false });
      }
    });

    // Carregar dados após inicializar o form
    if (this.postId) {
      // Se temos o post no state, usar diretamente
      if (statePost && statePost.id === this.postId) {
        console.log('[BlogForm] Usando post do state:', statePost);
        console.log('[BlogForm] Content do state:', statePost.content);
        // Sempre tentar buscar o conteúdo completo, pois o state pode não ter
        this.loadPost(this.postId);
      } else {
        // Caso contrário, buscar da API (fallback)
        this.loadPost(this.postId);
      }
    }
  }

  loadPost(id: string) {
    console.log('[BlogForm] Carregando post ID:', id);
    // Como o endpoint GET por ID não está disponível (405), vamos buscar pela lista
    this.svc.getPosts(1, 100).subscribe({
      next: (response: any) => {
        const posts = response.data || response;
        const post = posts.find((p: any) => p.id === id);
        
        if (!post) {
          console.error('[BlogForm] Post não encontrado na lista');
          this.notification.showError('Artigo não encontrado');
          this.router.navigate(['/admin/blog']);
          return;
        }
        
        console.log('[BlogForm] Post carregado da lista:', post);
        
        // Se o post não tem conteúdo (campo vazio na listagem), tentar buscar pelo slug
        if (!post.content && post.slug) {
          console.log('[BlogForm] Content vazio, buscando pelo slug:', post.slug);
          this.svc.getPostBySlug(post.slug).subscribe({
            next: (fullPost: any) => {
              console.log('[BlogForm] Post completo carregado:', fullPost);
              this.fillForm(fullPost);
            },
            error: (err) => {
              console.warn('[BlogForm] Erro ao carregar pelo slug, usando dados da lista', err);
              this.fillForm(post);
            }
          });
        } else {
          this.fillForm(post);
        }
      },
      error: (err) => {
        console.error('[BlogForm] erro ao carregar lista', err);
        this.notification.showError('Erro ao carregar artigo');
      }
    });
  }

  fillForm(post: any) {
    console.log('[BlogForm] fillForm - post recebido:', post);
    console.log('[BlogForm] fillForm - content:', post.content);
    const tags = Array.isArray(post.tags) ? post.tags.join(', ') : '';
    this.form.patchValue({
      title: post.title || '',
      slug: post.slug || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      featuredImage: post.featuredImage || '',
      author: post.author || '',
      publishedAt: post.publishedAt ? new Date(post.publishedAt).toISOString().slice(0, 16) : '',
      category: post.category || 'dicas',
      tags: tags,
      metaTitle: post.metaTitle || '',
      metaDescription: post.metaDescription || '',
      isPublished: post.isPublished ?? true
    }, { emitEvent: false });
    this.form.markAsPristine();
    console.log('[BlogForm] Form atualizado - content no form:', this.form.get('content')?.value);
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    const formData = { ...this.form.value };

    // Converter tags de string para array
    if (formData.tags && typeof formData.tags === 'string') {
      formData.tags = formData.tags.split(',').map((t: string) => t.trim()).filter((t: string) => t);
    } else {
      formData.tags = [];
    }

    const request = this.isEditMode
      ? this.svc.updatePost(this.postId!, formData)
      : this.svc.createPost(formData);

    request.subscribe({
      next: () => {
        this.saving = false;
        this.notification.showSuccess(this.isEditMode ? 'Artigo atualizado com sucesso' : 'Artigo criado com sucesso');
        this.router.navigate(['/admin/blog']);
      },
      error: (err) => {
        this.saving = false;
        console.error('[BlogForm] erro ao salvar', err);
        const message = err?.error?.message || 'Erro ao salvar artigo';
        this.notification.showError(message);
      }
    });
  }

  cancel() {
    this.router.navigate(['/admin/blog']);
  }

  openPreview() {
    this.showPreview = true;
  }

  closePreview() {
    this.showPreview = false;
  }

  get previewContent(): SafeHtml {
    const content = this.form.get('content')?.value || '';
    return this.sanitizer.bypassSecurityTrustHtml(content);
  }
}
